import { create } from 'zustand';
import { DraftPrices, LineKey, NetworkDraftPrices, NetworkFilterKey, NetworkFilterValues, NetworkPricesPagination, ProductsToBePricedId } from './types';

type NetworkPricesState = {
  expandedClusterId: string | null;
  selectedLineKeys: LineKey[];
  draftPrices: DraftPrices;
  pendingCellIds: ProductsToBePricedId[];
  networkDraftPrices: NetworkDraftPrices;
  pendingLineKeys: LineKey[];
  isTableRemounting: boolean;
  pagination: NetworkPricesPagination;
  filters: NetworkFilterValues;
  // Invalidates asynchronous filter work even when the selection was already empty.
  filterResetVersion: number;
  // Text typed in each chip search; lives here so "Limpar filtros" resets it, as in the price manager.
  filterSearch: Partial<Record<NetworkFilterKey, string>>;
};

type NetworkPricesActions = {
  toggleExpandedCluster: (clusterId: string) => void;
  toggleLineSelection: (key: LineKey) => void;
  setSelectedLineKeys: (keys: LineKey[]) => void;
  clearSelection: () => void;
  setDraftPrice: (cellId: ProductsToBePricedId, value: number | null) => void;
  clearDraftPrice: (cellId: ProductsToBePricedId) => void;
  startPendingCell: (cellId: ProductsToBePricedId) => void;
  finishPendingCell: (cellId: ProductsToBePricedId) => void;
  setNetworkDraftPrice: (lineKey: LineKey, value: number | null) => void;
  clearNetworkDraftPrice: (lineKey: LineKey) => void;
  // The network edit replaces every cluster of the line, so their unsaved drafts go with it.
  clearLineDrafts: (lineKey: LineKey, cellIds: ProductsToBePricedId[]) => void;
  startPendingLine: (lineKey: LineKey) => void;
  finishPendingLine: (lineKey: LineKey) => void;
  startTableRemount: () => void;
  finishTableRemount: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  // A new filter changes the result set: back to the first page, without a selection from the previous one.
  setFilter: (key: NetworkFilterKey, values: string[]) => void;
  clearFilters: () => void;
  setFilterSearch: (key: NetworkFilterKey, query: string) => void;
  reset: () => void;
};

export const initialState: NetworkPricesState = {
  expandedClusterId: null,
  selectedLineKeys: [],
  draftPrices: {},
  pendingCellIds: [],
  networkDraftPrices: {},
  pendingLineKeys: [],
  isTableRemounting: false,
  pagination: { page: 0, size: 25 },
  filters: {},
  filterResetVersion: 0,
  filterSearch: {},
};

// A new page size or filter changes the result set: back to the first page, without a selection from the previous one.
const firstPageWithoutSelection = (pagination: NetworkPricesPagination): Pick<NetworkPricesState, 'pagination' | 'selectedLineKeys'> => ({
  pagination: { ...pagination, page: 0 },
  selectedLineKeys: [],
});

export const useNetworkPricesStore = create<NetworkPricesState & NetworkPricesActions>()(set => ({
  ...initialState,
  toggleExpandedCluster: clusterId => set(state => ({ expandedClusterId: state.expandedClusterId === clusterId ? null : clusterId })),
  toggleLineSelection: key =>
    set(state => ({
      selectedLineKeys: state.selectedLineKeys.includes(key)
        ? state.selectedLineKeys.filter(selectedKey => selectedKey !== key)
        : [...state.selectedLineKeys, key],
    })),
  setSelectedLineKeys: keys => set({ selectedLineKeys: [...keys] }),
  clearSelection: () => set({ selectedLineKeys: [] }),
  setDraftPrice: (cellId, value) => set(state => ({ draftPrices: { ...state.draftPrices, [cellId]: value } })),
  clearDraftPrice: cellId =>
    set(state => {
      const draftPrices = { ...state.draftPrices };
      delete draftPrices[cellId];
      return { draftPrices };
    }),
  startPendingCell: cellId => set(state => (state.pendingCellIds.includes(cellId) ? state : { pendingCellIds: [...state.pendingCellIds, cellId] })),
  finishPendingCell: cellId => set(state => ({ pendingCellIds: state.pendingCellIds.filter(pendingId => pendingId !== cellId) })),
  setNetworkDraftPrice: (lineKey, value) => set(state => ({ networkDraftPrices: { ...state.networkDraftPrices, [lineKey]: value } })),
  clearNetworkDraftPrice: lineKey =>
    set(state => {
      const networkDraftPrices = { ...state.networkDraftPrices };
      delete networkDraftPrices[lineKey];
      return { networkDraftPrices };
    }),
  clearLineDrafts: (lineKey, cellIds) =>
    set(state => {
      const draftPrices = { ...state.draftPrices };
      const networkDraftPrices = { ...state.networkDraftPrices };
      cellIds.forEach(cellId => delete draftPrices[cellId]);
      delete networkDraftPrices[lineKey];
      return { draftPrices, networkDraftPrices };
    }),
  startPendingLine: lineKey => set(state => (state.pendingLineKeys.includes(lineKey) ? state : { pendingLineKeys: [...state.pendingLineKeys, lineKey] })),
  finishPendingLine: lineKey => set(state => ({ pendingLineKeys: state.pendingLineKeys.filter(pendingKey => pendingKey !== lineKey) })),
  startTableRemount: () => set({ isTableRemounting: true }),
  finishTableRemount: () => set({ isTableRemounting: false }),
  setPage: page => set(state => ({ pagination: { ...state.pagination, page }, selectedLineKeys: [] })),
  setPageSize: size => set(state => firstPageWithoutSelection({ ...state.pagination, size })),
  setFilter: (key, values) => set(state => ({ filters: { ...state.filters, [key]: values }, ...firstPageWithoutSelection(state.pagination) })),
  clearFilters: () =>
    set(state => ({ filters: {}, filterSearch: {}, filterResetVersion: state.filterResetVersion + 1, ...firstPageWithoutSelection(state.pagination) })),
  setFilterSearch: (key, query) => set(state => ({ filterSearch: { ...state.filterSearch, [key]: query } })),
  reset: () => set(state => ({ ...initialState, filterResetVersion: state.filterResetVersion + 1 })),
}));

// A line is locked while its network edit or any of its cluster edits is saving.
export const isLinePending = (state: NetworkPricesState, lineKey: LineKey, cellIds: ProductsToBePricedId[]): boolean =>
  state.pendingLineKeys.includes(lineKey) || cellIds.some(cellId => state.pendingCellIds.includes(cellId));
